import { tr, useArkmeLocale, arkmeIntlLocale } from './locale.js'
import { ArkmeRightPanelHeader } from './ArkmeRightPanelHeader.js'
import { compareTimelineMessages } from './timeline-message-order.js'
import { useCallback, useEffect, useId, useRef, useState, type CSSProperties } from 'react'
import { useResizableNoteDetail } from './use-resizable-note-detail.js'
import { ARKME_CONVERSATION_HEADER_HEIGHT } from './arkme-layout.js'
import type { ArkmeInterwovenDetail, ArkmeInterwovenMention, ArkmeMemberEvent, ArkmeRelatedQuickNoteItem, ArkmeSourceItem, ArkmeTimelineItem } from '../types.js'
import { ArkmeMark } from './ArkmeFooterAction.js'
import {
  ArkmeRelatedQuickNoteDetail,
  ArkmeRelatedQuickNotesCard,
  ArkmeRelatedQuickNotesList,
  type ArkmeRelatedDrawerView,
  type ArkmeRelatedQuickNoteDetailState,
  type ArkmeRelatedQuickNotesLoadState,
} from './ArkmeRelatedQuickNotes.js'
import { useArkmeAvatarImage } from './use-arkme-avatar-image.js'
import { ArkmeInterwovenReadReceipt } from './ArkmeInterwovenReadReceipt.js'
import { ArkmeCalendarDateTooltip } from './ArkmeCalendarDateTooltip.js'

export type ArkmeConversationRow =
  | { kind: 'message'; id: string; occurredAtMillis: number; item: ArkmeTimelineItem }
  | { kind: 'moment'; id: string; occurredAtMillis: number; item: ArkmeInterwovenMention }
  | { kind: 'member-event'; id: string; occurredAtMillis: number; item: ArkmeMemberEvent }

/** Pure deterministic merge; each authority keeps its own identity and duplicate policy. */
export function mergeConversationRows(
  messages: readonly ArkmeTimelineItem[],
  moments: readonly ArkmeInterwovenMention[],
  memberEvents: readonly ArkmeMemberEvent[] = [],
): ArkmeConversationRow[] {
  const messageById = new Map(messages.map(item => [item.itemUid, item]))
  const momentById = new Map(moments.map(item => [item.momentId, item]))
  const memberEventById = new Map(memberEvents.map(item => [item.eventId, item]))
  const rows: ArkmeConversationRow[] = [
    ...[...messageById.values()].map(item => ({
      kind: 'message' as const, id: `message:${item.itemUid}`,
      occurredAtMillis: item.sendAtMillis, item,
    })),
    ...[...momentById.values()].map(item => ({
      kind: 'moment' as const, id: `moment:${item.momentId}`,
      occurredAtMillis: item.occurredAtMillis, item,
    })),
    ...[...memberEventById.values()].map(item => ({
      kind: 'member-event' as const, id: `member-event:${item.eventId}`,
      occurredAtMillis: item.occurredAtMillis, item,
    })),
  ]
  return rows.sort((left, right) => left.kind === 'message' && right.kind === 'message'
    ? compareTimelineMessages(left.item, right.item)
    : left.occurredAtMillis - right.occurredAtMillis || left.id.localeCompare(right.id))
}

/** Match the desktop prelude policy while preserving each card's identity. */
export function projectInterwovenWindow(
  messages: readonly ArkmeTimelineItem[],
  moments: readonly ArkmeInterwovenMention[],
  hasMoreMessages: boolean,
  includeFromMillis?: number,
): { prelude: ArkmeInterwovenMention[]; inline: ArkmeInterwovenMention[] } {
  const sorted = [...new Map(moments.map(moment => [moment.momentId, moment])).values()]
    .sort((a, b) => a.occurredAtMillis - b.occurredAtMillis || a.momentId.localeCompare(b.momentId))
  const oldest = messages.length === 0 ? Infinity : Math.min(...messages.map(message => message.sendAtMillis))
  const isInline = (moment: ArkmeInterwovenMention) => moment.occurredAtMillis > oldest
    || includeFromMillis !== undefined && moment.occurredAtMillis >= includeFromMillis
  const prelude = sorted.filter(moment => !isInline(moment))
  const inline = sorted.filter(isInline)
  return { prelude: hasMoreMessages && inline.length > 0 ? [] : prelude, inline }
}

export function ArkmeInterwovenPrelude({ moments, onOpen }: {
  moments: readonly ArkmeInterwovenMention[]
  onOpen: (moment: ArkmeInterwovenMention) => void
}) {
  useArkmeLocale()
  const [expanded, setExpanded] = useState(false)
  const hiddenCount = Math.max(0, moments.length - 2)
  return <ul data-arkme-interwoven-prelude style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
    {!expanded && hiddenCount > 0 && <li style={styles.momentRow}>
      <button type="button" data-arkme-interwoven-expand aria-expanded={false}
        style={styles.card} onClick={() => { setExpanded(true) }}>{tr("展开另外")}{hiddenCount}{tr("条更早互动")}</button>
    </li>}
    {(expanded ? moments : moments.slice(-2)).map(moment => <ArkmeInterwovenMentionCard
      key={moment.momentId} moment={moment} rowId={`moment:${moment.momentId}`} onOpen={onOpen} />)}
  </ul>
}

export function interwovenTimeLabel(value: number, now = Date.now()): string {
  const date = new Date(value)
  const current = new Date(now)
  const time = new Intl.DateTimeFormat(arkmeIntlLocale(), {
    hour: '2-digit', minute: '2-digit', hour12: false,
  }).format(date)
  const dayStart = new Date(current.getFullYear(), current.getMonth(), current.getDate()).getTime()
  const targetStart = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime()
  if (targetStart === dayStart) return time
  if (targetStart === dayStart - 24 * 60 * 60 * 1000) return tr("昨天 {v0}", { v0: time })
  const day = new Intl.DateTimeFormat(arkmeIntlLocale(), { month: '2-digit', day: '2-digit' }).format(date)
  return `${day} ${time}`
}

export function interwovenDetailTimeLabel(value: number): string {
  const date = new Date(value)
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  const hour = String(date.getHours()).padStart(2, '0')
  const minute = String(date.getMinutes()).padStart(2, '0')
  return `${month}月${day}日 ${hour}:${minute}`
}

export interface InterwovenContentPart {
  text: string
  mention: boolean
}

/** Keeps mention styling presentational; business identity still comes only from the owner DTO. */
export function interwovenContentParts(text: string): InterwovenContentPart[] {
  const parts = text.split(/(@[^\s@，,。；;：:！!?？、]+)/gu).filter(Boolean)
  return parts.map(part => ({ text: part, mention: part.startsWith('@') }))
}

/** Returns a target only when the already-authorized directory has one exact group match. */
export function resolveInterwovenGroupTarget(
  sources: readonly ArkmeSourceItem[],
  groupName: string,
): ArkmeSourceItem | undefined {
  const matches = sources.filter(source => source.kind === 'group_chat' && source.displayName === groupName)
  return matches.length === 1 ? matches[0] : undefined
}

const styles: Record<string, CSSProperties> = {
  momentRow: { width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 },
  momentTime: { color: 'var(--dsw-alias-label-tertiary, #9097a1)', fontSize: 12, lineHeight: '18px' },
  card: {
    maxWidth: 'min(680px, 100%)', minWidth: 0, border: 0, padding: '4px 8px', margin: 0,
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
    borderRadius: 8, background: 'transparent', color: 'var(--dsw-alias-label-secondary, #737982)',
    fontFamily: 'var(--dsw-font-family, inherit)', fontSize: 13, lineHeight: '20px', cursor: 'pointer',
  },
  avatar: {
    width: 18, height: 18, minWidth: 18, overflow: 'hidden', borderRadius: 999,
    display: 'grid', placeItems: 'center', background: 'var(--dsw-alias-bg-subtle, #f0f2f5)',
  },
  avatarImage: { width: '100%', height: '100%', display: 'block', objectFit: 'cover' },
  cardText: { minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textAlign: 'left' },
  originLabel: {
    minWidth: 0, maxWidth: 'min(9em, 24%)', flex: '0 0 auto', boxSizing: 'border-box',
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
    padding: '0 5px', borderRadius: 4, fontSize: 11, lineHeight: '18px',
    background: 'var(--dsw-alias-bg-layer-2, #f3f4f6)',
    color: 'var(--dsw-alias-label-tertiary, #9097a1)',
  },
  chevron: { width: 15, height: 15, flex: 'none', color: 'var(--dsw-alias-label-tertiary, #9097a1)' },
  aside: {
    position: 'absolute', zIndex: 8, top: ARKME_CONVERSATION_HEADER_HEIGHT, right: 0, bottom: 0, width: 'min(372px, 100%)',
    height: 'auto', minHeight: 0,
    display: 'flex', flexDirection: 'column', boxSizing: 'border-box',
    borderLeft: '1px solid #e5e6e9',
    background: '#fff', boxShadow: '-12px 0 28px rgba(29,32,40,.055)',
  },
  asideBody: { flex: 1, minHeight: 0, overflowY: 'auto', padding: '12px 18px 22px' },
  detailSender: { display: 'flex', alignItems: 'center', gap: 10 },
  detailSenderText: { minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 },
  detailSenderName: {
    color: '#17191c', fontSize: 14, lineHeight: '20px', fontWeight: 600,
  },
  detailTime: { color: '#92959d', fontSize: 11, lineHeight: '16px' },
  detailText: {
    margin: '18px 2px 0', color: '#343840',
    whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', fontSize: 14, lineHeight: '24px',
  },
  mention: { color: 'var(--dsw-alias-state-business-primary, #3964fe)' },
  groupTag: {
    width: '100%', maxWidth: '100%', marginTop: 10, padding: '11px 12px', border: 0,
    borderRadius: 12, display: 'inline-flex', alignItems: 'center', gap: 9, boxSizing: 'border-box',
    background: 'transparent', color: '#68707c', fontSize: 12,
    lineHeight: '18px', cursor: 'pointer', fontFamily: 'var(--dsw-font-family, inherit)',
  },
  groupTagText: { minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  groupTagIcon: { width: 14, height: 14, flex: 'none' },
  groupTagChevron: { fontSize: 15, lineHeight: '14px' },
  state: { color: 'var(--dsw-alias-label-secondary, #68707c)', fontSize: 13, lineHeight: '20px' },
  retry: {
    marginTop: 12, padding: '6px 12px', border: '1px solid var(--dsw-alias-border-l2, #e2e5e9)',
    borderRadius: 8, background: 'transparent', color: 'var(--dsw-alias-label-primary, #17191c)', cursor: 'pointer',
  },
}

function OpaqueAvatar({ avatarRef, size = 18 }: { avatarRef?: string; size?: number }) {
  useArkmeLocale()
  const src = useArkmeAvatarImage(avatarRef) ?? ''
  return <span data-arkme-avatar style={{ ...styles.avatar, width: size, height: size, minWidth: size }} aria-hidden>
    {src === '' ? <ArkmeMark size={size} /> : <img src={src} alt="" draggable={false} style={styles.avatarImage} />}
  </span>
}

/** Inline identity only; keep the row distinct from a regular chat bubble. */
function InterwovenSenderAvatar({ avatarRef, name }: { avatarRef: string | undefined; name: string }) {
  const src = useArkmeAvatarImage(avatarRef)
  const [failedSrc, setFailedSrc] = useState<string>()
  const initial = Array.from(name.trim())[0] || '?'
  return <span data-arkme-interwoven-avatar aria-hidden style={{
    ...styles.avatar, width: 20, height: 20, minWidth: 20, flex: 'none',
    fontSize: 11, lineHeight: '20px', fontWeight: 500,
    color: 'var(--dsw-alias-label-secondary, #737982)',
  }}>
    {src && src !== failedSrc
      ? <img key={src} src={src} alt="" draggable={false} style={styles.avatarImage}
        onError={() => { setFailedSrc(src) }} />
      : initial}
  </span>
}

export function ArkmeInterwovenMentionCard({
  moment,
  rowId,
  onOpen,
  highlighted = false,
}: {
  moment: ArkmeInterwovenMention
  rowId?: string
  onOpen: (moment: ArkmeInterwovenMention) => void
  highlighted?: boolean
}) {
  useArkmeLocale()
  const [hintAnchor, setHintAnchor] = useState<HTMLButtonElement>()
  const hintId = useId()
  const closeHint = useCallback(() => { setHintAnchor(undefined) }, [])
  useEffect(closeHint, [moment.momentRef, closeHint])
  const summary = moment.summary.trim() || tr('群聊提及')
  const accessible = `${moment.groupName}，${moment.senderName}：${summary}`
  return <li style={styles.momentRow} data-arkme-interwoven-card={moment.momentId} data-arkme-conversation-row={rowId}>
    <time style={styles.momentTime} dateTime={new Date(moment.occurredAtMillis).toISOString()}>
      {interwovenTimeLabel(moment.occurredAtMillis)}
    </time>
    <button
      type="button"
      data-arkme-interwoven-summary
      style={{ ...styles.card, ...(highlighted ? { background: 'var(--dsw-alias-bg-active, #eef0fa)', outline: '1px solid var(--dsw-alias-state-business-primary, #a5acff)' } : {}) }}
      aria-label={tr("打开快记详情：{v0}", { v0: accessible })}
      aria-describedby={hintAnchor ? hintId : undefined}
      onMouseOver={event => {
        // The receipt has its own reader-specific hint; never stack both hints.
        if ((event.target as Element).closest?.('[data-arkme-interwoven-receipt]')) closeHint()
        else setHintAnchor(event.currentTarget)
      }}
      onMouseLeave={closeHint}
      onFocus={event => {
        event.currentTarget.style.boxShadow = '0 0 0 2px var(--dsw-alias-state-business-primary, #3964fe)'
        if (event.target === event.currentTarget) setHintAnchor(event.currentTarget)
        else closeHint()
      }}
      onBlur={event => { event.currentTarget.style.boxShadow = 'none'; closeHint() }}
      onClick={() => { closeHint(); onOpen(moment) }}
    >
      <InterwovenSenderAvatar key={moment.senderAvatarRef || moment.senderName}
        avatarRef={moment.senderAvatarRef} name={moment.senderName} />
      <span data-arkme-interwoven-content style={styles.cardText}>{summary}</span>
      <span data-arkme-interwoven-origin style={styles.originLabel}>{moment.groupName || tr('群聊')}</span>
      <ArkmeInterwovenReadReceipt moment={moment} />
      <svg viewBox="0 0 16 16" style={styles.chevron} aria-hidden>
        <path d="m6 3 5 5-5 5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </button>
    {hintAnchor && <ArkmeCalendarDateTooltip anchor={hintAnchor} id={hintId} text={accessible} onClose={closeHint} />}
  </li>
}

export type ArkmeInterwovenDetailViewState =
  | { kind: 'loading' }
  | { kind: 'success'; detail: ArkmeInterwovenDetail }
  | { kind: 'error'; message: string }

export function ArkmeInterwovenDetailAside({
  state,
  relatedView = 'source-detail',
  relatedState = { kind: 'idle' },
  relatedDetailState = { kind: 'idle' },
  onClose,
  onRetry,
  onOpenGroup,
  onOpenRelated,
  onSelectRelated,
  onBackRelated,
  onRetryRelated,
  onRetryRelatedDetail,
  sourceRef,
  shareWebsite,
}: {
  state: ArkmeInterwovenDetailViewState
  relatedView?: ArkmeRelatedDrawerView
  relatedState?: ArkmeRelatedQuickNotesLoadState
  relatedDetailState?: ArkmeRelatedQuickNoteDetailState
  onClose: () => void
  onRetry: () => void
  onOpenGroup?: () => void
  onOpenRelated?: () => void
  onSelectRelated?: (item: ArkmeRelatedQuickNoteItem) => void
  onBackRelated?: () => void
  onRetryRelated?: () => void
  onRetryRelatedDetail?: (item: ArkmeRelatedQuickNoteItem) => void
  sourceRef?: string
  shareWebsite?: string
}) {
  useArkmeLocale()
  const asideBodyRef = useRef<HTMLDivElement>(null)
  const detailPanelRef = useRef<HTMLElement>(null)
  const resize = useResizableNoteDetail(detailPanelRef)
  const scrollTopByViewRef = useRef<Record<ArkmeRelatedDrawerView, number>>({
    'source-detail': 0,
    'related-list': 0,
    'related-detail': 0,
  })
  const sourceDetailKey = state.kind === 'success' ? state.detail.momentId : ''
  useEffect(() => {
    scrollTopByViewRef.current = { 'source-detail': 0, 'related-list': 0, 'related-detail': 0 }
    if (asideBodyRef.current !== null) asideBodyRef.current.scrollTop = 0
  }, [sourceDetailKey])
  useEffect(() => {
    if (asideBodyRef.current !== null) asideBodyRef.current.scrollTop = scrollTopByViewRef.current[relatedView]
  }, [relatedView, sourceDetailKey])
  const rememberCurrentScroll = () => {
    if (asideBodyRef.current !== null) scrollTopByViewRef.current[relatedView] = asideBodyRef.current.scrollTop
  }
  const detailContent = state.kind === 'success'
    ? state.detail.textContent.trim() || (state.detail.degraded ? '' : state.detail.title.trim())
    : ''
  const relatedTotal = relatedState.kind === 'success' ? relatedState.list.total : 0
  const title = relatedView === 'related-list'
    ? tr("{v0} 条相关快记", { v0: String(relatedTotal) })
    : relatedView === 'related-detail' ? '相关快记详情' : '快记详情'
  return <aside ref={detailPanelRef} style={{ ...styles.aside, ...resize.style }} aria-label={tr("快记详情")} data-arkme-interwoven-detail>
    {resize.handle}
    <ArkmeRightPanelHeader title={title} onClose={onClose} closeLabel={tr("关闭快记详情")}
      onBack={relatedView === 'source-detail' ? undefined : () => { rememberCurrentScroll(); onBackRelated?.() }}
      backLabel={relatedView === 'related-detail' ? tr("返回相关快记列表") : tr("返回快记详情")} />
    <div ref={asideBodyRef} style={styles.asideBody} aria-live="polite" data-arkme-interwoven-aside-body>
      {relatedView === 'related-list'
        ? <ArkmeRelatedQuickNotesList state={relatedState}
          onSelect={item => {
            rememberCurrentScroll()
            onSelectRelated?.(item)
          }} onRetry={onRetryRelated ?? (() => undefined)} />
        : relatedView === 'related-detail'
          ? <ArkmeRelatedQuickNoteDetail state={relatedDetailState}
            onRetry={onRetryRelatedDetail ?? (() => undefined)}
            {...(sourceRef === undefined ? {} : { sourceRef })}
            {...(shareWebsite === undefined ? {} : { shareWebsite })} />
          : state.kind === 'loading' ? <div style={styles.state} role="status">{tr("正在加载快记详情…")}</div>
        : state.kind === 'error' ? <div style={styles.state} role="alert">
          <div>{state.message}</div>
          <button type="button" style={styles.retry} onClick={onRetry}>{tr("重试")}</button>
        </div> : <>
          <div style={styles.detailSender}>
            <OpaqueAvatar {...(state.detail.senderAvatarRef === undefined ? {} : { avatarRef: state.detail.senderAvatarRef })} size={40} />
            <div style={styles.detailSenderText}>
              <span style={styles.detailSenderName}>{state.detail.senderName}</span>
              <time style={styles.detailTime} dateTime={new Date(state.detail.occurredAtMillis).toISOString()}>
                {interwovenDetailTimeLabel(state.detail.occurredAtMillis)}
              </time>
            </div>
          </div>
          {detailContent === '' && state.detail.degraded
            ? <div style={{ ...styles.state, marginTop: 16 }}>{tr("这条快记正文暂时不可用，请稍后重试。")}</div>
            : <p style={styles.detailText} data-arkme-interwoven-degraded={state.detail.degraded || undefined}>
              {(detailContent === '' ? [{ text: '暂无文本内容', mention: false }] : interwovenContentParts(detailContent))
                .map((part, index) => <span key={`${index}:${part.text}`} style={part.mention ? styles.mention : undefined}>{part.text}</span>)}
            </p>}
          <button
            type="button"
            style={{ ...styles.groupTag, ...(onOpenGroup === undefined ? { cursor: 'not-allowed', opacity: .65 } : {}) }}
            disabled={onOpenGroup === undefined}
            aria-label={tr("打开群聊：{v0}", { v0: state.detail.groupName })}
            title={onOpenGroup === undefined ? '当前会话列表中无法唯一定位该群聊' : tr("打开群聊：{v0}", { v0: state.detail.groupName })}
            data-arkme-interwoven-group-target={state.detail.groupName}
            onClick={onOpenGroup}
          >
            <svg viewBox="0 0 16 16" style={styles.groupTagIcon} aria-hidden>
              <path d="M3 2.5h8.5a2 2 0 0 1 2 2V11a2 2 0 0 1-2 2H3a1 1 0 0 1-1-1V3.5a1 1 0 0 1 1-1Z" fill="none" stroke="currentColor" strokeWidth="1.4" />
              <path d="M5 5.5h5M5 8h3.5" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
            </svg>
            <span style={styles.groupTagText}>{state.detail.groupName}</span>
            <span style={styles.groupTagChevron} aria-hidden>›</span>
          </button>
          <ArkmeRelatedQuickNotesCard state={relatedState}
            onOpen={() => { rememberCurrentScroll(); onOpenRelated?.() }}
            onRetry={onRetryRelated ?? (() => undefined)} />
        </>}
    </div>
  </aside>
}
